import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Flatpickr from 'react-flatpickr';
import "flatpickr/dist/flatpickr.min.css";

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);// [start, end]
    const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

    useEffect(() => {
        if (dateRange.length === 2 || dateRange.length === 0) {
            fetchDashboardData();
        }
    }, [dateRange]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            let url = '/organizer/dashboard';
            if (dateRange[0] && dateRange[1]) {
                const start = dateRange[0].toISOString().split('T')[0];
                const end = dateRange[1].toISOString().split('T')[0];
                url += `?start_date=${start}&end_date=${end}`;
            }

            const response = await api.get(url);
            console.log('Organizer Dashboard Response:', response.data);

            if (response.data && response.data.data) {
                setStats(response.data.data.stats || {
                    total_events: 0,
                    active_events: 0,
                    pending_events: 0,
                    total_revenue: 0,
                    tickets_sold: 0
                });

                if (Array.isArray(response.data.data.events)) {
                    setEvents(response.data.data.events);
                } else {
                    setEvents([]);
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error(error.response?.data?.message || 'Failed to load dashboard data');
            setEvents([]);
            setStats({
                total_events: 0,
                active_events: 0,
                pending_events: 0,
                total_revenue: 0,
                tickets_sold: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (eventId, currentStatus) => {
        const action = currentStatus ? 'Disable' : 'Enable';
        const result = await Swal.fire({
            title: `${action} Event`,
            text: `Are you sure you want to ${action.toLowerCase()} this event?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(`/organizer/events/${eventId}`, { status: !currentStatus });
            toast.success(`Event ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
            fetchDashboardData();
        } catch (error) {
            console.error('Error toggling event status:', error);
            toast.error('Failed to update event status');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        const result = await Swal.fire({
            title: 'Delete Event',
            text: 'Are you sure you want to delete this event? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/organizer/events/${eventId}`);
            toast.success('Event deleted successfully');
            fetchDashboardData();
        } catch (error) {
            console.error('Error deleting event:', error);
            toast.error('Failed to delete event');
        }
    };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

    const formatCurrency = (amount) => {
        return `${parseFloat(amount).toFixed(2)}`;
    };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h3 mb-0 text-gray-900 font-weight-bold">
          <i className="fas fa-tachometer-alt text-purple-500 mr-2"></i>
          Organizer Dashboard
        </h5>
          <div className="mb-3 d-flex align-items-center gap-2">
              <Flatpickr
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  options={{ mode: 'range', dateFormat: 'Y-m-d' }}
                  className="form-control"
                  placeholder="Select date range"
              />
              {dateRange.length > 0 && (
                  <button
                      className="btn !border-2 !border-black/10  ml-2 "
                      onClick={() => setDateRange([])}
                  >
                      Clear
                  </button>
              )}
          </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="row mb-4">
          {/* Total Events Card */}
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-left-yellow shadow h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                      Total Events
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total_events}</div>
                  </div>
                  <div className="col-auto">
                    <i className="fas fa-calendar fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Events Card */}
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-left-yellow shadow h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                      Active Events
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.active_events}</div>
                  </div>
                  <div className="col-auto">
                    <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tickets Sold Card */}
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-left-yellow shadow h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                      Tickets Sold
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.tickets_sold}</div>
                  </div>
                  <div className="col-auto">
                    <i className="fas fa-ticket-alt fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Revenue Card */}
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card border-left-yellow shadow h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                      Total Revenue
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {stats.total_revenue && Object.keys(stats.total_revenue).length > 0 ? (
                            Object.entries(stats.total_revenue).map(([currency, amount]) => (
                                <div key={currency}>
                                    {currency}: {formatCurrency(amount)}
                                </div>
                            ))
                        ) : (
                            formatCurrency(0)
                        )}
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-4">
        <Link to="/organizer/events/create" className="btn !text-white !font-weight-bold !bg-purple-500 shadow-sm hover:!bg-purple-600">
          <i className="fas fa-plus mr-2"></i>
          Create New Event
        </Link>
      </div>

      {/* Events List */}
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
          <h6 className="m-0 font-weight-bold text-gray-900">Upcoming Events</h6>
          <Link to="/organizer/events" className="btn btn-sm btn-outline !rounded-lg !border-2 !border-purple-500 !text-purple-500 hover:!bg-purple-500 hover:!text-white">
            View All <i className="fas fa-arrow-right ml-1"></i>
          </Link>
        </div>
        
        <div className="card-body">
          {events.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-calendar-alt fa-4x text-gray-300 mb-3"></i>
              <h5 className="text-gray-600 mb-2">No events yet</h5>
              <p className="text-muted mb-4">Get started by creating your first event</p>
              <Link to="/organizer/events/create" className="btn btn-lg !font-weight-bold !text-white !font-weight-bold !bg-purple-500 shadow-sm hover:!bg-purple-600">
                <i className="fas fa-plus mr-2"></i>
                Create Event
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                      <th>Image</th>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, 5).map((event) => (
                    <tr key={event.id}>
                        <td>
                            {event.images && event.images.length > 0 ? (
                                <img
                                    src={event.images[0].image_url}
                                    alt={event.title}
                                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 5 }}
                                />
                            ) : (
                                <div
                                    className="bg-gray-200 flex items-center justify-center"
                                    style={{ width: 50, height: 50, borderRadius: 5 }}
                                >
                                    <span>🎫</span>
                                </div>
                            )}
                        </td>
                        <td>
                            <div className="font-weight-bold text-gray-800">{event.title}</div>
                            {event.top_event && (
                                <span className="badge badge-primary mr-2">
                                        <i className="fas fa-fire mr-1"></i>
                                        Top
                                      </span>
                            )}
                            {event.featured_event && (
                                <span className="badge badge-warning badge-sm">
                            <i className="fas fa-star mr-1"></i>Featured
                          </span>
                            )}
                        </td>
                      <td>
                        <small className="text-muted">
                          <i className="far fa-calendar mr-1"></i>
                          {formatDate(event.event_start)}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {event.location}
                        </small>
                      </td>
                      <td>
                        <span className={`badge ${event.status ? 'badge-success' : 'badge-warning'}`}>
                          {event.status ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="btn-group" role="group">
                          <Link 
                            to={`/organizer/events/edit/${event.id}`} 
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Event"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          <Link 
                            to={`/events/${event.id}`} 
                            className="btn btn-sm btn-outline-info"
                            title="View Event"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          <button 
                            onClick={() => handleToggleStatus(event.id, event.status)}
                            className={`btn btn-sm ${event.status ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            title={event.status ? 'Disable Event' : 'Enable Event'}
                          >
                            <i className={`fas ${event.status ? 'fa-ban' : 'fa-check-circle'}`}></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteEvent(event.id)}
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Event"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

